/* eslint-disable flowtype/no-types-missing-file-annotation */
import { ReactNode, useState } from "react";

import classNames from "classnames";
import {
  UseComboboxState,
  UseComboboxStateChangeOptions,
  useCombobox,
} from "downshift";
import sortBy from "lodash/sortBy";
import pull from "lodash/pull";
import styled from "styled-components";
import { track } from "app/lib/segmentAnalytics";

import Badge from "app/components/shared/Badge";
import Icon from "app/components/shared/Icon";
import Dropdown from "app/components/shared/Dropdown";
import Button from "app/components/shared/Button";

const Menu = styled.ul<{ visible: boolean }>`
  visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
  width: 300px;
  max-height: 235px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 0px;
  margin-top: 4px;
  margin-bottom: 0px;
`;

const MenuItem = styled.li<{ highlighted: boolean }>`
  color: ${({ highlighted }) =>
    highlighted ? "var(--purple-600)" : "var(--charcoal-700)"};
  border-radius: 4px;
  cursor: pointer;
`;

const SearchIcon = styled(Icon).attrs({ icon: "search" })`
  flex: 0 0 auto;
  height: 20px;
  width: 20px;
  color: var(--gray-800);
`;

const StyledSearchInput = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? "flex" : "none")};
  align-items: center;
  height: 40px;
  max-width: 300px;
  border: 1px solid var(--gray-500);
  border-radius: 4px;
  margin: 0px 5px;
  padding: 1px 1px;

  &:focus-within {
    border-color: var(--lime-500);
    border-width: 2px;
    padding: 0px 0px;

    & ${SearchIcon} {
      color: var(--charcoal-700);
    }
  }

  & input {
    all: unset;
    height: 100%;
    width: 100%;
    padding-right: 5px;

    ::placeholder {
      color: var(--gray-700);
    }
  }
`;

const StyledButton = styled(Button).attrs({
  className: "truncate",
})`
  transition: box-shadow 0.2s;
  outline: none;

  &:focus-visible {
    position: relative;
    z-index: 10; /* ensures that the box shadow stacks on top of neighbouring elements in a list view */
    box-shadow:
      0 0 0 2px #fff,
      0 0 0 4px var(--lime-500);
  }
`;

type MenuItemLinkProps = {
  children: ReactNode;
  href: string;
  id: string;
  isActive: boolean;
  onClick?: () => void;
};

const MenuItemLink = ({
  children,
  isActive,
  href,
  id,
  onClick,
}: MenuItemLinkProps) => (
  <a
    href={href}
    className={classNames(
      "btn black hover-purple focus-purple flex items-center semi-bold p2 mx1 my0 rounded",
      {
        "btn--activated purple bg-light-purple semi-bold": isActive,
      },
    )}
    data-testid={`pipeline-branch-filter-option-${id}`}
    onClick={onClick}
  >
    {children}
  </a>
);

function getBranchesFilter(inputValue: string | undefined) {
  return function branchFilter(branch: string) {
    return !inputValue || branch.includes(inputValue.toLowerCase());
  };
}

function stateReducer(
  state: UseComboboxState<string>,
  actionAndChanges: UseComboboxStateChangeOptions<string>,
) {
  const { type, changes } = actionAndChanges;

  switch (type) {
    // prevent input value update on selection because a user has clicked on a link
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      return {
        ...changes,
        inputValue: state.inputValue,
      };
    default:
      return changes;
  }
}

export type Props = {
  branches: Array<string>;
  currentBranch?: string;
  defaultBranch: string | null;
  pipelineUrl: string;
  pipelineBuildsUrl: string;
};

// TODO this component can be removed in favour of BuildsFilterDropdown when we remove the
// `BUILDS_FILTERING_PIPELINE_BUILDS` feature flag
function BranchPicker(props: Props) {
  let initialBranchList: Array<string>;
  if (props.defaultBranch) {
    const notDefaultBranches = sortBy(
      pull(props.branches, props.defaultBranch),
    );
    initialBranchList = [props.defaultBranch, ...notDefaultBranches];
  } else {
    initialBranchList = props.branches;
  }

  const [filteredBranches, setFilteredBranches] =
    useState<Array<string>>(initialBranchList);
  const showSearchInput = initialBranchList.length > 5;

  const {
    inputValue,
    selectedItem,
    highlightedIndex,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
  } = useCombobox({
    items: filteredBranches,
    initialInputValue: "",
    defaultSelectedItem: props.currentBranch,
    onInputValueChange({ inputValue }) {
      setFilteredBranches(
        initialBranchList.filter(getBranchesFilter(inputValue)),
      );
    },
    stateReducer,
  });

  const handleSubmit = () => {
    return (window.location.href = `${window.location.origin}${
      props.pipelineBuildsUrl
    }?branch=${encodeURIComponent(inputValue)}`);
  };

  return (
    <>
      <div className="border-bottom border-gray py0 pb1 mb1">
        <MenuItemLink
          key="all"
          id="all"
          href={props.pipelineUrl}
          isActive={!selectedItem}
        >
          All Branches
        </MenuItemLink>
      </div>

      <form>
        <StyledSearchInput visible={showSearchInput}>
          <label className="pl2 pr1" {...getLabelProps()}>
            <SearchIcon />
            <span className="hide">Search branches</span>
          </label>

          <input
            className="input"
            placeholder="Search recent branches"
            {...getInputProps({
              onKeyDown(event) {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSubmit();
                }
              },
            })}
          />
        </StyledSearchInput>
      </form>

      <Menu {...getMenuProps()} visible={true}>
        {filteredBranches.map((branch, index) => (
          <MenuItem
            key={branch}
            highlighted={highlightedIndex === index}
            {...getItemProps({ item: branch, index })}
          >
            <MenuItemLink
              id={branch}
              href={`${props.pipelineBuildsUrl}?branch=${branch}`}
              isActive={selectedItem === branch}
              onClick={() =>
                _trackItemClicked({
                  action: "view-branch",
                  text: branch,
                  href: `${props.pipelineBuildsUrl}?branch=${branch}`,
                })
              }
            >
              <span
                className={classNames({
                  truncate: branch === props.defaultBranch,
                })}
                style={{
                  wordBreak:
                    branch !== props.defaultBranch && branch.includes("_")
                      ? "break-all"
                      : undefined,
                }}
              >
                {branch}
              </span>

              {branch === props.defaultBranch && (
                <Badge
                  outline={true}
                  style={{ color: "inherit" }}
                  className={classNames({
                    "badge--purple": selectedItem === props.defaultBranch,
                  })}
                >
                  Default
                </Badge>
              )}
            </MenuItemLink>
          </MenuItem>
        ))}

        {filteredBranches.length === 0 && (
          <div className="flex flex-column items-center justify-center p4">
            <p className="bold">Nothing here</p>
            <p className="dark-gray center mb3">
              We show the most recent 50 branches here. Older branches can be
              found using their direct URL
            </p>

            <a
              className="btn btn-default flex items-center px2 py1 line-height-2"
              href={`${props.pipelineBuildsUrl}?branch=${encodeURIComponent(
                inputValue,
              )}`}
              onClick={() =>
                _trackItemClicked({
                  action: "view-branch-as-searched",
                  text: "Go to branch",
                  href: `${props.pipelineBuildsUrl}?branch=${encodeURIComponent(
                    inputValue,
                  )}`,
                  theme: "default",
                })
              }
            >
              Go to branch
            </a>
          </div>
        )}
      </Menu>
    </>
  );
}

export default function BranchPickerDropdownWrapper(props: Props) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <Dropdown
      className="block"
      width={300}
      nibOffsetX={-125}
      offsetY={8}
      style={{ maxWidth: "300px" }}
      onToggle={(isOpen: boolean) => {
        setIsDropdownOpen(isOpen);
      }}
    >
      <StyledButton data-testid="pipeline-builds-filter-trigger" theme="small">
        <div className="flex items-center gap1 cursor-pointer">
          <Icon
            icon="custom/outline/16x/branch"
            className="flex-none"
            style={{ width: 16, height: 16 }}
          />

          <span className="semi-bold m0 truncate line-height-2">
            {props.currentBranch ?? "All"}
          </span>

          <Icon
            icon={isDropdownOpen ? "up-triangle" : "down-triangle"}
            className="flex-none"
            style={{ width: 7, height: 7 }}
          />
        </div>
      </StyledButton>

      <BranchPicker {...props} />
    </Dropdown>
  );
}

const _trackItemClicked = ({
  action,
  text,
  href = "",
  theme = "",
}: {
  action: string;
  text: string;
  href?: string;
  theme?: string;
}) => {
  track("Pipelines Branch Filter Item Clicked", {
    action,
    text,
    source: "Builds Filter Dropdown",
    href,
    theme,
  });
};
